"use strict";
import { TradeType } from "@uniswap/sdk-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { areCurrencyIdsEqual, currencyId } from "utils/uniswapCurrencyId";
import { useBscQuote } from "features/routing/hooks/useBscQuote";
import { useTopazApiQuote } from "features/routing/hooks/useTopazApiQuote";
import { use0xQuote } from "features/routing/hooks/use0xQuote";
import { useOpenOceanQuote } from "features/routing/hooks/useOpenOceanQuote";
import { useBinanceQuote } from "features/routing/hooks/useBinanceQuote";
import { applyOpenOceanRankingPenalty } from "features/openocean/constants";
import { useTransactionSettingsContext } from "features/transactions/settings/contexts/TransactionSettingsContext";
import {
  isAggregatorBlockedForTokens,
  resolveRouteSources
} from "features/transactions/swap/utils/routingProvider";
function tradeSourceId(trade) {
  const t = trade;
  if (t.isTopazApi) {
    return "topazApi";
  }
  if (t.is0x) {
    return "zeroX";
  }
  if (t.isOpenOcean) {
    return "openOcean";
  }
  if (t.isBinance) {
    return "binance";
  }
  return "topaz";
}
function tradeOutputRaw(trade) {
  const outputAmount = trade?.outputAmount;
  return outputAmount ? BigInt(outputAmount.quotient.toString()) : 0n;
}
export function useTrade({
  amountSpecified: amount,
  otherCurrency,
  tradeType,
  skip,
  isDebouncing
}) {
  const { routeSources: sources } = useTransactionSettingsContext();
  const routeSources = resolveRouteSources(sources);
  const currencyIn = tradeType === TradeType.EXACT_INPUT ? amount?.currency : otherCurrency;
  const currencyOut = tradeType === TradeType.EXACT_OUTPUT ? amount?.currency : otherCurrency;
  const currencyInEqualsCurrencyOut = currencyIn && currencyOut && areCurrencyIdsEqual(currencyId(currencyIn), currencyId(currencyOut));
  const isZeroAmount = amount?.quotient.toString() === "0";
  const baseSkip = skip || !amount || isZeroAmount || currencyInEqualsCurrencyOut || !currencyIn || !currencyOut;
  const bscTokenIn = currencyIn?.isNative ? currencyIn.wrapped.address : currencyIn?.address;
  const bscTokenOut = currencyOut?.isNative ? currencyOut.wrapped.address : currencyOut?.address;
  const bscAmount = amount?.quotient.toString();
  const bothV2V3Disabled = !routeSources.v2 && !routeSources.v3;
  const allSourcesDisabled = bothV2V3Disabled && !routeSources.topazApi && !routeSources.zeroX && !routeSources.openOcean && !routeSources.binance;
  const topazResult = useBscQuote({
    tokenIn: bscTokenIn,
    tokenOut: bscTokenOut,
    amountSpecified: bscAmount,
    tradeType,
    chainId: currencyIn?.chainId,
    skip: baseSkip || bothV2V3Disabled,
    skipV2: !routeSources.v2,
    skipV3: !routeSources.v3
  });
  const topazApiResult = useTopazApiQuote({
    tokenIn: bscTokenIn,
    tokenOut: bscTokenOut,
    amountSpecified: bscAmount,
    tradeType,
    chainId: currencyIn?.chainId,
    skip: baseSkip || !routeSources.topazApi || tradeType !== TradeType.EXACT_INPUT || isAggregatorBlockedForTokens("topazApi", bscTokenIn, bscTokenOut),
    currencyIn,
    currencyOut
  });
  const zeroXResult = use0xQuote({
    tokenIn: bscTokenIn,
    tokenOut: bscTokenOut,
    amountSpecified: bscAmount,
    tradeType,
    chainId: currencyIn?.chainId,
    skip: baseSkip || !routeSources.zeroX || tradeType !== TradeType.EXACT_INPUT || isAggregatorBlockedForTokens("zeroX", bscTokenIn, bscTokenOut),
    currencyIn,
    currencyOut
  });
  const openOceanResult = useOpenOceanQuote({
    tokenIn: bscTokenIn,
    tokenOut: bscTokenOut,
    amountSpecified: bscAmount,
    tradeType,
    chainId: currencyIn?.chainId,
    skip: baseSkip || !routeSources.openOcean || tradeType !== TradeType.EXACT_INPUT || isAggregatorBlockedForTokens("openOcean", bscTokenIn, bscTokenOut),
    currencyIn,
    currencyOut
  });
  const binanceResult = useBinanceQuote({
    tokenIn: bscTokenIn,
    tokenOut: bscTokenOut,
    amountSpecified: bscAmount,
    tradeType,
    chainId: currencyIn?.chainId,
    skip: baseSkip || !routeSources.binance || tradeType !== TradeType.EXACT_INPUT || isAggregatorBlockedForTokens("binance", bscTokenIn, bscTokenOut),
    currencyIn,
    currencyOut
  });
  const topazRefetchRef = useRef(topazResult.refetch);
  topazRefetchRef.current = topazResult.refetch;
  const topazApiRefetchRef = useRef(topazApiResult.refetch);
  topazApiRefetchRef.current = topazApiResult.refetch;
  const zeroXRefetchRef = useRef(zeroXResult.refetch);
  zeroXRefetchRef.current = zeroXResult.refetch;
  const openOceanRefetchRef = useRef(openOceanResult.refetch);
  openOceanRefetchRef.current = openOceanResult.refetch;
  const binanceRefetchRef = useRef(binanceResult.refetch);
  binanceRefetchRef.current = binanceResult.refetch;
  const refetchAll = useCallback(() => {
    topazRefetchRef.current();
    topazApiRefetchRef.current();
    zeroXRefetchRef.current();
    openOceanRefetchRef.current();
    binanceRefetchRef.current();
  }, []);
  const prevSourcesRef = useRef(routeSources);
  useEffect(() => {
    const prev = prevSourcesRef.current;
    if (prev.v2 !== routeSources.v2 || prev.v3 !== routeSources.v3 || prev.topazApi !== routeSources.topazApi || prev.zeroX !== routeSources.zeroX || prev.openOcean !== routeSources.openOcean || prev.binance !== routeSources.binance) {
      prevSourcesRef.current = routeSources;
      refetchAll();
    }
  }, [routeSources, refetchAll]);
  const lastTradeRef = useRef(null);
  const lastTradeParamsKeyRef = useRef(null);
  const lastTradeSourceRef = useRef(null);
  const lastQuoteUpdatedAtRef = useRef(null);
  const paramsKey = [
    currencyIn ? currencyId(currencyIn) : "",
    currencyOut ? currencyId(currencyOut) : "",
    amount?.quotient.toString() ?? "",
    tradeType
  ].join("|");
  const GRACE_MS = 2e3;
  const fetchStartRef = useRef(0);
  const [graceTick, forceRender] = useState(0);
  const graceTimerRef = useRef(null);
  const isExactInput = tradeType === TradeType.EXACT_INPUT;
  const topazLoading = !bothV2V3Disabled && topazResult.isLoading;
  const topazApiLoading = routeSources.topazApi && isExactInput && topazApiResult.isLoading;
  const zeroXLoading = routeSources.zeroX && isExactInput && zeroXResult.isLoading;
  const openOceanLoading = routeSources.openOcean && isExactInput && openOceanResult.isLoading;
  const binanceLoading = routeSources.binance && isExactInput && binanceResult.isLoading;
  const allDone = !topazLoading && !topazApiLoading && !zeroXLoading && !openOceanLoading && !binanceLoading;
  const anyLoading = topazLoading || topazApiLoading || zeroXLoading || openOceanLoading || binanceLoading;
  const graceParamsKeyRef = useRef(paramsKey);
  useEffect(() => {
    if (graceParamsKeyRef.current !== paramsKey) {
      graceParamsKeyRef.current = paramsKey;
      fetchStartRef.current = 0;
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
      }
    }
    if (anyLoading && fetchStartRef.current === 0) {
      fetchStartRef.current = Date.now();
      graceTimerRef.current = setTimeout(() => {
        forceRender((n) => n + 1);
      }, GRACE_MS);
    }
    if (allDone) {
      fetchStartRef.current = 0;
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
      }
    }
  }, [anyLoading, allDone, paramsKey]);
  useEffect(() => {
    return () => {
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    };
  }, []);
  return useMemo(() => {
    void graceTick;
    if (lastTradeParamsKeyRef.current !== paramsKey) {
      lastTradeRef.current = null;
      lastTradeParamsKeyRef.current = null;
      lastTradeSourceRef.current = null;
      lastQuoteUpdatedAtRef.current = null;
    }
    if (allSourcesDisabled && !baseSkip) {
      return {
        trade: null,
        isLoading: false,
        isFetching: false,
        error: new Error("All routing sources are disabled. Enable at least one source in swap settings."),
        refetch: refetchAll,
        quoteUpdatedAt: null
      };
    }
    if (isDebouncing && !baseSkip) {
      return {
        trade: null,
        isLoading: true,
        isFetching: true,
        error: null,
        refetch: refetchAll,
        quoteUpdatedAt: null
      };
    }
    const candidates = [];
    const TOPAZ_DIRECT_WIN_MARGIN_BPS = 10n;
    const topazApiOut = tradeOutputRaw(topazApiResult.trade);
    if (topazApiResult.trade) {
      candidates.push({
        trade: topazApiResult.trade,
        quoteUpdatedAt: topazApiResult.quoteUpdatedAt
      });
    }
    if (topazResult.trade) {
      const directOut = tradeOutputRaw(topazResult.trade);
      const defersToApi = topazApiOut > 0n && directOut * 10000n <= topazApiOut * (10000n + TOPAZ_DIRECT_WIN_MARGIN_BPS);
      if (!defersToApi) {
        candidates.push({ trade: topazResult.trade, quoteUpdatedAt: topazResult.quoteUpdatedAt });
      }
    }
    if (zeroXResult.trade) {
      candidates.push({ trade: zeroXResult.trade, quoteUpdatedAt: zeroXResult.quoteUpdatedAt });
    }
    if (openOceanResult.trade) {
      candidates.push({
        trade: openOceanResult.trade,
        quoteUpdatedAt: openOceanResult.quoteUpdatedAt,
        isOpenOcean: true
      });
    }
    if (binanceResult.trade) {
      candidates.push({
        trade: binanceResult.trade,
        quoteUpdatedAt: binanceResult.quoteUpdatedAt
      });
    }
    const hasPartialResult = candidates.length > 0;
    const withinGrace = fetchStartRef.current > 0 && Date.now() - fetchStartRef.current < GRACE_MS;
    const shouldWait = anyLoading && hasPartialResult && withinGrace && !lastTradeRef.current;
    let bestTrade = null;
    let bestQuoteUpdatedAt = null;
    let bestOut = -1n;
    for (const candidate of candidates) {
      const rawOut = tradeOutputRaw(candidate.trade);
      const out = candidate.isOpenOcean ? applyOpenOceanRankingPenalty(rawOut) : rawOut;
      if (out > bestOut) {
        bestOut = out;
        bestTrade = candidate.trade;
        bestQuoteUpdatedAt = candidate.quoteUpdatedAt;
      }
    }
    if (bestTrade && allDone) {
      lastTradeRef.current = bestTrade;
      lastTradeParamsKeyRef.current = paramsKey;
      lastTradeSourceRef.current = tradeSourceId(bestTrade);
      lastQuoteUpdatedAtRef.current = bestQuoteUpdatedAt;
    }
    const sourceLoading = {
      topaz: topazLoading,
      topazApi: topazApiLoading,
      zeroX: zeroXLoading,
      openOcean: openOceanLoading,
      binance: binanceLoading
    };
    const displayedProviderPending = lastTradeSourceRef.current ? sourceLoading[lastTradeSourceRef.current] : false;
    if (anyLoading && lastTradeRef.current && (withinGrace || !hasPartialResult || displayedProviderPending)) {
      return {
        trade: lastTradeRef.current,
        isLoading: true,
        isFetching: true,
        error: null,
        refetch: refetchAll,
        quoteUpdatedAt: lastQuoteUpdatedAtRef.current
      };
    }
    if (shouldWait || anyLoading && !hasPartialResult) {
      return {
        trade: null,
        isLoading: true,
        isFetching: true,
        error: null,
        refetch: refetchAll,
        quoteUpdatedAt: null
      };
    }
    if (bestTrade) {
      lastTradeRef.current = bestTrade;
      lastTradeParamsKeyRef.current = paramsKey;
      lastTradeSourceRef.current = tradeSourceId(bestTrade);
      lastQuoteUpdatedAtRef.current = bestQuoteUpdatedAt;
    }
    return {
      trade: bestTrade,
      // `isLoading` means "no usable trade yet" — it must NOT track pending stragglers
      // once a best trade is displayed, or the confirm-time re-quote gate (which
      // requires !isLoading within its 8s window) fails every submission whenever one
      // source is hung. Background activity is `isFetching`, which only drives the
      // shimmer and gas-info debouncing.
      isLoading: false,
      isFetching: anyLoading,
      error: !bestTrade ? topazResult.error || topazApiResult.error || zeroXResult.error || openOceanResult.error || binanceResult.error : null,
      refetch: refetchAll,
      quoteUpdatedAt: bestQuoteUpdatedAt
    };
  }, [
    topazResult,
    topazApiResult,
    zeroXResult,
    openOceanResult,
    binanceResult,
    refetchAll,
    anyLoading,
    allDone,
    allSourcesDisabled,
    baseSkip,
    isDebouncing,
    paramsKey,
    graceTick,
    topazLoading,
    topazApiLoading,
    zeroXLoading,
    openOceanLoading,
    binanceLoading
  ]);
}
